import { supabase, checkSupabaseHealth } from './supabaseClient';
import { createDocumentIdentifier } from './progressService';
import { trackEvent } from './analyticsService';
import axios from 'axios';

// Note: getSessionId not used in this service but kept for consistency

const FREE_LIBRARY_DOC_CAP = 3;
const FREE_LIBRARY_TOTAL_BYTES_CAP = 25 * 1024 * 1024;

const makeCodedError = (code, message, extras = {}) => {
    const err = new Error(message);
    err.code = code;
    Object.assign(err, extras);
    return err;
};

const resolveTierForUser = async () => {
    try {
        const { data, error } = await supabase.auth.getSession();
        if (error) return 'free';
        const token = data?.session?.access_token;
        if (!token) return 'free';
        const response = await axios.get('/api/usage', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const tier = response?.data?.tier;
        return typeof tier === 'string' && tier ? tier : 'free';
    } catch {
        return 'free';
    }
};

// Upload document to Supabase Storage and save metadata
export const uploadDocument = async (file, user = null) => {
    try {
        trackEvent('document_upload_started', {
            fileType: file?.type || 'unknown',
            fileSize: typeof file?.size === 'number' ? file.size : null,
            hasUser: !!user,
        });

        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        // Only allow authenticated users to store files
        if (!user) {
            throw new Error('Authentication required for file storage');
        }

        const tier = await resolveTierForUser();
        if (tier !== 'premium') {
            const { data: existing, count, error: existingError } = await supabase
                .from('user_documents')
                .select('file_size', { count: 'exact' })
                .eq('user_id', user.id);

            if (existingError) {
                throw new Error(`Failed to check library limits: ${existingError.message}`);
            }

            const docCount = typeof count === 'number' ? count : (existing || []).length;
            if (docCount >= FREE_LIBRARY_DOC_CAP) {
                throw makeCodedError(
                    'LIBRARY_DOC_CAP_REACHED',
                    `Free plan allows up to ${FREE_LIBRARY_DOC_CAP} saved documents. Upgrade to save more.`,
                    { limit: FREE_LIBRARY_DOC_CAP, current: docCount }
                );
            }

            const totalBytes = (existing || []).reduce((sum, row) => sum + (typeof row?.file_size === 'number' ? row.file_size : 0), 0);
            if (totalBytes + (typeof file?.size === 'number' ? file.size : 0) > FREE_LIBRARY_TOTAL_BYTES_CAP) {
                throw makeCodedError(
                    'LIBRARY_SIZE_CAP_REACHED',
                    'Free plan storage is full. Upgrade to save larger libraries.',
                    { limitBytes: FREE_LIBRARY_TOTAL_BYTES_CAP, currentBytes: totalBytes }
                );
            }
        }

        // Create document identifier
        const documentInfo = createDocumentIdentifier(file);

        // Dedup: if this exact file already exists in the user's library, return it
        const { data: existingDoc } = await supabase
            .from('user_documents')
            .select('*')
            .eq('user_id', user.id)
            .eq('document_hash', documentInfo.hash)
            .limit(1)
            .maybeSingle();

        if (existingDoc) {
            // Touch last_opened so it surfaces in recents
            await supabase
                .from('user_documents')
                .update({ last_opened: new Date().toISOString() })
                .eq('id', existingDoc.id);

            return {
                success: true,
                document: { ...existingDoc, last_opened: new Date().toISOString() },
                documentInfo,
                alreadyExisted: true,
            };
        }

        // Create unique filename with user ID prefix
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop();
        const storedFilename = `${user.id}/${timestamp}_${documentInfo.hash}.${fileExtension}`;
        
        // Upload file to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
            .from('user-documents')
            .upload(storedFilename, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (uploadError) {
            console.error('Upload error:', uploadError);
            throw new Error(`Failed to upload file: ${uploadError.message}`);
        }

        // Save document metadata to database
        const documentRecord = {
            user_id: user.id,
            document_hash: documentInfo.hash,
            original_filename: file.name,
            stored_filename: storedFilename,
            file_type: file.type,
            file_size: file.size,
            storage_path: uploadData.path,
            upload_date: new Date().toISOString(),
            last_opened: new Date().toISOString(),
            is_favorite: false
        };

        const { data: metadataData, error: metadataError } = await supabase
            .from('user_documents')
            .insert([documentRecord])
            .select()
            .single();

        if (metadataError) {
            // If metadata save fails, clean up uploaded file
            await supabase.storage
                .from('user-documents')
                .remove([storedFilename]);
            throw new Error(`Failed to save document metadata: ${metadataError.message}`);
        }

        trackEvent('document_upload_succeeded', {
            fileType: file?.type || 'unknown',
            fileSize: typeof file?.size === 'number' ? file.size : null,
            hasUser: !!user,
        });

        console.log('Document uploaded successfully:', metadataData);
        return {
            success: true,
            document: metadataData,
            documentInfo: documentInfo
        };

    } catch (error) {
        trackEvent('document_upload_failed', {
            fileType: file?.type || 'unknown',
            fileSize: typeof file?.size === 'number' ? file.size : null,
            hasUser: !!user,
        });
        console.error('Error uploading document:', error);
        throw error;
    }
};

export const getDocumentById = async (documentId, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            throw new Error('Authentication required');
        }

        const { data, error } = await supabase
            .from('user_documents')
            .select('*')
            .eq('id', documentId)
            .eq('user_id', user.id)
            .single();

        if (error) {
            throw new Error(`Failed to fetch document: ${error.message}`);
        }

        return { document: data };
    } catch (error) {
        console.error('Error fetching document:', error);
        throw error;
    }
};

// Get user's document library
export const getUserDocuments = async (user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            return { documents: [] };
        }

        const { data, error } = await supabase
            .from('user_documents')
            .select('*')
            .eq('user_id', user.id)
            .order('last_opened', { ascending: false });

        if (error) {
            throw new Error(`Failed to fetch documents: ${error.message}`);
        }

        return { documents: data || [] };

    } catch (error) {
        console.error('Error fetching user documents:', error);
        throw error;
    }
};

// Get recent documents (last 5)
export const getRecentDocuments = async (user = null, limit = 5) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            return { documents: [] };
        }

        const { data, error } = await supabase
            .from('user_documents')
            .select('*')
            .eq('user_id', user.id)
            .order('last_opened', { ascending: false })
            .limit(limit);

        if (error) {
            throw new Error(`Failed to fetch recent documents: ${error.message}`);
        }

        return { documents: data || [] };

    } catch (error) {
        console.error('Error fetching recent documents:', error);
        throw error;
    }
};

// Download document from storage
export const downloadDocument = async (document, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user || user.id !== document.user_id) {
            throw new Error('Unauthorized access to document');
        }

        let signedUrl = null;

        // Try client-side signed URL first
        const { data, error } = await supabase.storage
            .from('user-documents')
            .createSignedUrl(document.stored_filename, 3600); // 1 hour expiry

        if (!error && data?.signedUrl) {
            signedUrl = data.signedUrl;
        } else {
            // Fallback: use server-side endpoint (handles shared files & RLS restrictions)
            console.log('Client-side signed URL failed, trying server-side fallback...');
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;
            if (!token) {
                throw new Error('Authentication required for document access');
            }

            const resp = await axios.post('/api/usage?signedUrl=1', {
                storedFilename: document.stored_filename,
                documentId: document.id,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (resp.data?.signedUrl) {
                signedUrl = resp.data.signedUrl;
            } else {
                throw new Error('Failed to get download URL from server');
            }
        }

        // Update last_opened timestamp
        await supabase
            .from('user_documents')
            .update({ last_opened: new Date().toISOString() })
            .eq('id', document.id);

        return {
            success: true,
            url: signedUrl,
            document: document
        };

    } catch (error) {
        console.error('Error downloading document:', error);
        throw error;
    }
};

// Delete document and its file
export const deleteDocument = async (documentId, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            throw new Error('Authentication required');
        }

        // Get document info first
        const { data: document, error: fetchError } = await supabase
            .from('user_documents')
            .select('*')
            .eq('id', documentId)
            .eq('user_id', user.id)
            .single();

        if (fetchError || !document) {
            throw new Error('Document not found or unauthorized');
        }

        // Delete file from storage
        const { error: storageError } = await supabase.storage
            .from('user-documents')
            .remove([document.stored_filename]);

        if (storageError) {
            console.warn('Failed to delete file from storage:', storageError);
        }

        // Delete metadata from database
        const { error: dbError } = await supabase
            .from('user_documents')
            .delete()
            .eq('id', documentId)
            .eq('user_id', user.id);

        if (dbError) {
            throw new Error(`Failed to delete document: ${dbError.message}`);
        }

        return { success: true };

    } catch (error) {
        console.error('Error deleting document:', error);
        throw error;
    }
};

// Toggle favorite status
export const toggleDocumentFavorite = async (documentId, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            throw new Error('Authentication required');
        }

        // Get current favorite status
        const { data: document, error: fetchError } = await supabase
            .from('user_documents')
            .select('is_favorite')
            .eq('id', documentId)
            .eq('user_id', user.id)
            .single();

        if (fetchError) {
            throw new Error('Document not found or unauthorized');
        }

        // Toggle favorite status
        const { data, error } = await supabase
            .from('user_documents')
            .update({ is_favorite: !document.is_favorite })
            .eq('id', documentId)
            .eq('user_id', user.id)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to update favorite status: ${error.message}`);
        }

        return { success: true, document: data };

    } catch (error) {
        console.error('Error toggling favorite:', error);
        throw error;
    }
};

// Rename document
export const renameDocument = async (documentId, newName, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        if (!user) {
            throw new Error('Authentication required');
        }

        const { data, error } = await supabase
            .from('user_documents')
            .update({ original_filename: newName })
            .eq('id', documentId)
            .eq('user_id', user.id)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to rename document: ${error.message}`);
        }

        return { success: true, document: data };

    } catch (error) {
        console.error('Error renaming document:', error);
        throw error;
    }
};
